import type { Impersonation, Session } from '@/shared/api';

import { tourCopy } from './copy';

/**
 * De ankers waar de rondleiding naar wijst: `data-tour="…"` op een vaste plek in
 * de schil of op het dashboard. De rail zet `nav-<sleutel>` op elk item (de
 * sleutels uit `layouts/nav-model.ts`), zodat een nieuw item vanzelf een anker heeft.
 */
export const TOUR_ANCHORS = {
    navDashboard: 'nav-dashboard',
    navMyDay: 'nav-my-day',
    navPlanning: 'nav-planning',
    navInspections: 'nav-inspections',
    navBilling: 'nav-billing',
    navSettings: 'nav-settings',
    newInspection: 'new-inspection',
    sync: 'sync',
    account: 'account',
    /** De knop Menu bij een smal scherm, waar de rail is ingeklapt. */
    menu: 'menu',
} as const;

export type TourAnchor = (typeof TOUR_ANCHORS)[keyof typeof TOUR_ANCHORS];

export type TourStepKey = keyof typeof tourCopy.steps;

export interface TourStep {
    key: TourStepKey;
    anchor: TourAnchor;
    /**
     * Staat het anker in de rail? Dan wijst de stap bij een smal scherm naar de
     * knop Menu, met de zin dat je het daar vindt. Anders, zonder anker in beeld,
     * staat de stap als kaart in het midden.
     */
    inRail: boolean;
    title: string;
    body: string;
}

function step(key: TourStepKey, anchor: TourAnchor, inRail = true): TourStep {
    return { key, anchor, inRail, ...tourCopy.steps[key] };
}

const OWNER: readonly TourStep[] = [
    step('dashboard', TOUR_ANCHORS.navDashboard),
    step('newInspection', TOUR_ANCHORS.newInspection, false),
    step('inspections', TOUR_ANCHORS.navInspections),
    step('planning', TOUR_ANCHORS.navPlanning),
    step('billing', TOUR_ANCHORS.navBilling),
    step('settings', TOUR_ANCHORS.navSettings),
];

/** De eigenaar die zelf keurt: Mijn dag in plaats van Keuringen, zodat het er zes blijven. */
const OWNER_IN_THE_FIELD: readonly TourStep[] = [
    step('dashboard', TOUR_ANCHORS.navDashboard),
    step('newInspection', TOUR_ANCHORS.newInspection, false),
    step('myDay', TOUR_ANCHORS.navMyDay),
    step('planning', TOUR_ANCHORS.navPlanning),
    step('billing', TOUR_ANCHORS.navBilling),
    step('settings', TOUR_ANCHORS.navSettings),
];

/** De inspecteur op de tablet: zijn dag, een opname, en dat het zonder internet werkt. */
const INSPECTOR: readonly TourStep[] = [
    step('myDay', TOUR_ANCHORS.navMyDay),
    step('startSurvey', TOUR_ANCHORS.navInspections),
    step('sync', TOUR_ANCHORS.sync),
    step('account', TOUR_ANCHORS.account),
];

/**
 * De stappen voor deze sessie, naar de rollen in de omgeving waar je nu werkt.
 *
 * Geen rondleiding zonder omgeving (een platformbeheerder die nergens werkt) en
 * niet tijdens meekijken: een beheerder die meekijkt hoort de rondleiding van
 * een ander niet af te ronden. De rollen komen rechtstreeks uit de sessie, niet
 * uit `hasRole`: die laat een platformbeheerder overal door.
 */
export function tourStepsFor(
    session: Pick<Session, 'tenant' | 'roles'> | null,
    impersonation: Impersonation | null,
): readonly TourStep[] {
    if (!session?.tenant || impersonation) {
        return [];
    }

    const owner = session.roles.includes('owner');
    const field = session.roles.includes('inspector');

    if (owner) {
        return field ? OWNER_IN_THE_FIELD : OWNER;
    }

    return field ? INSPECTOR : [];
}
